/**
 * Cooperatives client (web) — coop COLLECTION POINT for proximity matching.
 *
 * HAND-ROLLED on the shared `fseAxios` mutator (same pattern as buyers.ts /
 * market.ts): orval isn't regenerated for /coop/me/collection-point yet, so these
 * two functions are coded directly against the authoritative API DTO. They go
 * through the same authed axios mutator the generated clients use (Bearer +
 * 401-refresh + FseApiError envelope) and return the unwrapped response body.
 *
 * PRIVACY: the endpoint stores the coop's collection point at full WGS84 precision
 * but NEVER returns the coordinates — the response carries only
 * `hasCollectionPoint`. The matcher derives a coarse distance band from the point
 * server-side; the capture UI only needs to know whether it is set.
 */
import { fseAxios } from './client';

/** GET/PATCH /coop/me/collection-point response — privacy-safe (no coordinates). */
export interface CollectionPoint {
  cooperativeId: string;
  hasCollectionPoint: boolean;
}

/** A WGS84 point to set as the coop's collection point. */
export interface CollectionPointGps {
  lat: number;
  lng: number;
}

/**
 * Read whether the caller's cooperative has a collection point set. Authed
 * (coop_admin/admin). 400 if the caller admins 0 or >1 coops (ambiguous target).
 */
export function getMyCollectionPoint(signal?: AbortSignal): Promise<CollectionPoint> {
  return fseAxios<CollectionPoint>({
    url: '/coop/me/collection-point',
    method: 'GET',
    signal,
  });
}

/**
 * Set (or clear, with null) the caller's cooperative collection point. The point
 * is stored full-precision but never echoed back — the response is just
 * hasCollectionPoint. Authed (coop_admin/admin).
 */
export function setMyCollectionPoint(
  location: CollectionPointGps | null,
): Promise<CollectionPoint> {
  return fseAxios<CollectionPoint>({
    url: '/coop/me/collection-point',
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    data: { location },
  });
}
